<template>
    <div ref="root">
        <div v-if="noDocument">
            <div v-if="isAgreement">
                <prime-message
                    severity="danger"
                    :closable="false"
                >
                    {{ t('purchaseContract.documentSection.somethingWrong') }}
                </prime-message>
            </div>

            <div
                v-else-if="isKiosk"
                class="mt-4"
            >
                <prime-button
                    :label="inlineCheckoutLanguage"
                    class="min-w-[200px]"
                    severity="success"
                    :loading="isSigningDocument"
                    @click="emit('inlineCheckout')"
                />
            </div>

            <div
                v-else
                class="mt-4 flex justify-end gap-4"
            >
                <prime-button
                    :label="t('purchaseContract.addToCart')"
                    :disabled="isCreatingCheckout"
                    @click="handleAddToCart"
                />

                <prime-button
                    :label="t('purchaseContract.checkoutNow')"
                    :loading="isCreatingCheckout"
                    @click="handleCheckout"
                />
            </div>
        </div>

        <!--Inline Document Mode-->
        <purchase-contract-section v-else-if="displayMode === 'INLINE_MODE'">
            <template #title>
                {{ title }}
            </template>

            <document-section-form
                v-if="!contractHasPricing"
                :body="bodyValue"
                :signature-mode="signatureMode"
                :invalid-initials="invalidInitials"
                :invalid-signatures="invalidSignatures"
                @document-input="handleSubmitNoPricingContract"
            >
                <div class="mt-4 flex justify-end">
                    <prime-button
                        type="submit"
                        :label="
                            isAgreement
                                ? t(
                                      'purchaseContract.documentSection.submitAgreement'
                                  )
                                : t('purchaseContract.completeBooking')
                        "
                        :loading="activatePending"
                    />
                </div>
            </document-section-form>

            <document-section-form
                v-else
                :body="bodyValue"
                :signature-mode="signatureMode"
                :invalid-initials="invalidInitials"
                :invalid-signatures="invalidSignatures"
                @document-input="handleCompleteDocumentOrCheckout"
            >
                <div
                    v-if="isKiosk"
                    class="mt-4 flex justify-end gap-4"
                >
                    <prime-button
                        type="submit"
                        class="min-w-[200px]"
                        :label="inlineCheckoutLanguage"
                        name="submitType"
                        value="payNow"
                        severity="success"
                        :loading="isSigningDocument"
                    />
                </div>

                <div
                    v-else
                    class="mt-4 flex justify-end gap-4"
                >
                    <prime-button
                        type="submit"
                        :label="t('purchaseContract.addToCart')"
                        name="submitType"
                        value="addToCart"
                        :disabled="isSigningDocumentCheckoutNow"
                        :loading="isSigningDocumentPending"
                    />

                    <prime-button
                        type="submit"
                        :label="t('purchaseContract.checkoutNow')"
                        name="submitType"
                        value="checkout"
                        :loading="isSigningDocumentCheckoutNow"
                        :disabled="isSigningDocumentPending"
                    />
                </div>
            </document-section-form>
        </purchase-contract-section>

        <!--Document Display Mode-->
        <purchase-contract-section v-else>
            <template #title>
                {{ title }}
            </template>

            <div
                v-if="!documentCompleted"
                class="text-right"
            >
                <prime-button
                    :label="
                        t('purchaseContract.documentSection.completeDocument')
                    "
                    @click="display = true"
                />
            </div>

            <template v-else>
                <prime-message
                    severity="success"
                    :closable="false"
                >
                    {{
                        t('purchaseContract.documentSection.documentCompleted')
                    }}
                </prime-message>

                <div
                    v-if="!contractHasPricing"
                    class="mt-6 flex justify-end gap-8"
                >
                    <prime-button
                        :label="
                            isAgreement
                                ? t(
                                      'purchaseContract.documentSection.submitAgreement'
                                  )
                                : t('purchaseContract.completeBooking')
                        "
                        severity="success"
                        :loading="activatePending"
                        @click="handleSubmit"
                    />
                </div>

                <div
                    v-else-if="isKiosk"
                    class="mt-6 flex justify-end"
                >
                    <prime-button
                        type="submit"
                        class="md:w-[200px]"
                        :label="inlineCheckoutLanguage"
                        name="submitType"
                        value="payNow"
                        severity="success"
                        :loading="isSigningDocument"
                    />
                </div>

                <div
                    v-else
                    class="mt-6 flex justify-end gap-8"
                >
                    <prime-button
                        :label="t('purchaseContract.addToCart')"
                        class="p-button-outlined"
                        :disabled="isCreatingCheckout"
                        @click="handleAddToCart"
                    />

                    <prime-button
                        :label="t('purchaseContract.checkoutNow')"
                        :loading="isCreatingCheckout"
                        @click="handleCheckout"
                    />
                </div>
            </template>
        </purchase-contract-section>

        <prime-dialog
            v-model:visible="display"
            :header="title"
            :pt="{
                root: {
                    class: 'w-full max-h-[100vh] md:max-h-[90vh] !rounded-none md:h-auto md:!w-[80vw] md:!w-[80vh]'
                },
                header: {
                    class: '!rounded-none'
                },
                content: {
                    class: '!rounded-none flex-1 '
                }
            }"
            :pt-options="{
                mergeProps: true
            }"
            :modal="true"
        >
            <document-section-form
                :body="bodyValue"
                :signature-mode="signatureMode"
                :saved-initials="savedDocumentInput?.initials ?? undefined"
                :saved-signatures="savedDocumentInput?.signatures ?? undefined"
                :invalid-initials="invalidInitials"
                :invalid-signatures="invalidSignatures"
                @document-input="handleDocumentInput"
            >
                <prime-button
                    class="mt-6"
                    type="submit"
                    :loading="isSigningDocument"
                    :label="t('done')"
                />
            </document-section-form>
        </prime-dialog>
    </div>
</template>

<script lang="ts">
import { getFragmentData, graphql } from '~/resources/graphql'

export const DocumentSectionContractFragment = graphql(/** GraphQL */ `
    fragment DocumentSectionContractFragment on PlanContract {
        id
        updatedAtUtc
        plan {
            planType
            name
        }
        planPresentation(language: $language) {
            title
        }
        isGuest
        documentCompleted
        documentTemplate(language: $language) {
            displayMode
            signatureMode
            title
            body
        }
        previewPricing {
            total
        }
        terms {
            billingType
        }
    }
`)
</script>

<script setup lang="ts">
import { onMounted, ref, computed, watch, toRef, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import PrimeButton from 'primevue/button'
import PrimeMessage from 'primevue/message'
import PrimeDialog from 'primevue/dialog'
import { useToast } from 'primevue/usetoast'
import type { FragmentType } from '~/resources/graphql'
import DocumentSectionForm, {
    type DocumentSectionFormInput
} from '~/components/PurchaseContract/DocumentSection/DocumentSectionForm.vue'
import {
    ContractReadyForPurchase,
    PlanDocumentSignatureMode
} from '../../../../resources/app/generated/enums'
import type {
    DocumentInput,
    SignDocumentInput
} from '~/resources/graphql/graphql'
import { useSignDocumentMutation } from '~/components/PurchaseContract/DocumentSection/document-section-mutations'
import { useActivateContractMutation } from '~/components/PurchaseContract/use-activate-contract-mutation'
import {
    setCartVisible,
    useCartVisible,
    useInvalidateCartQuery,
    useSetCartData
} from '~/composables/use-cart'
import { useRouteLink } from '~/composables/routing'
import { useActiveFacility } from '~/composables/use-active-facility'
import {
    useCreateCheckout,
    useSignDocumentCheckoutNowMutation
} from '~/components/Checkout/create-checkout-mutation'
import { useShowAlert } from '~/composables/use-show-alert'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import { Decimal } from '~/composables/support/decimal'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useKioskSuccess } from '~/composables/use-kiosk-success'
import { useStorefrontPrefix } from '~/composables/use-storefront-prefix'
import { useI18n } from 'vue-i18n'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { useAddToCartAnalytics } from '~/composables/analytics/add-to-cart'
import { useDocumentSectionAddToCartAnalyticsQuery } from '~/components/PurchaseContract/DocumentSection/document-section-add-to-cart-analytics-query'
import { useSelectedLanguage } from '~/composables/use-selected-language'

const props = withDefaults(
    defineProps<{
        contract: FragmentType<typeof DocumentSectionContractFragment>
        isSigningDocument: boolean
        invalidInitials: Set<number>
        invalidSignatures: Set<number>
        balanceDueMode?: boolean
    }>(),
    { balanceDueMode: false }
)

const emit = defineEmits<{
    (e: 'signDocument', payload: SignDocumentInput): void
    (e: 'addToCart'): void
    (e: 'inlineCheckout', payload: DocumentInput): void
    (e: 'inlineCheckoutDocument', payload: DocumentInput): void
}>()

const { t } = useI18n()
const root = ref<HTMLDivElement>()
onMounted(() => {
    // Only scroll to this section if we are on an agreement
    // Because on the kiosk if we are not an agreement, then we have an inline checkout section
    // That means scrolling to the document will scroll past the checkout section
    if (isAgreement.value || !isKiosk.value) {
        nextTick().then(() => {
            root.value?.scrollIntoView({
                behavior: 'smooth'
                // Intentionally not using block: 'center' here because if the document spans the entire page
                // than we'll skip the first part of the document
            })
        })
    }
})

const { isKiosk, isGroup, preserveModeLink } = useLayoutMode()
const contractData = computed(() =>
    getFragmentData(DocumentSectionContractFragment, props.contract)
)
const noDocument = contractData.value.documentTemplate === null
const displayMode = contractData.value.documentTemplate?.displayMode ?? ''
const signatureMode =
    contractData.value.documentTemplate?.signatureMode ??
    PlanDocumentSignatureMode.DRAW_ONLY
const bodyValue = contractData.value.documentTemplate?.body ?? ''
const title =
    contractData.value.documentTemplate?.title ??
    t('purchaseContract.documentSection.termsAndConditions')
// The document body/title are captured from the current fetch on setup, so submit
// that same fetch language even if the footer locale changes before submission.
const documentLanguage = useSelectedLanguage().value

function makeDocumentInput(
    documentInput: DocumentSectionFormInput
): DocumentInput {
    return {
        title,
        language: documentLanguage,
        ...documentInput
    }
}

const isAgreement = computed(
    () => contractData.value.plan.planType === 'AGREEMENT'
)

const display = ref(false)
const savedDocumentInput = ref<DocumentInput>()
const documentCompleted = computed(
    () =>
        contractData.value.documentCompleted ||
        savedDocumentInput.value !== undefined
)

function handleDocumentInput({
    documentInput
}: {
    documentInput: DocumentSectionFormInput
}) {
    const resolvedDocumentInput = makeDocumentInput(documentInput)

    if (!contractHasPricing.value || isKiosk.value) {
        savedDocumentInput.value = resolvedDocumentInput
        if (isKiosk.value) {
            emit('inlineCheckoutDocument', resolvedDocumentInput)
        }
        display.value = false
    } else {
        emit('signDocument', {
            id: contractData.value.id,
            updatedAtUtc: contractData.value.updatedAtUtc,
            documentInput: resolvedDocumentInput
        })
    }
}

const { mutate: signDocument, isPending: isSigningDocumentPending } =
    useSignDocumentMutation()

const {
    mutate: signDocumentCheckoutNow,
    isPending: isSigningDocumentCheckoutNow
} = useSignDocumentCheckoutNowMutation()

const activeFacility = useActiveFacility()
const cartVisible = useCartVisible()
const setCartData = useSetCartData()
const { emitAddToCartEvent } = useAddToCartAnalytics()
const { mutateAsync: getAddToCartAnalyticsContract } =
    useDocumentSectionAddToCartAnalyticsQuery()

async function handleAddToCart() {
    try {
        const addToCartResult = await getAddToCartAnalyticsContract(
            contractData.value.id
        )

        if (addToCartResult !== null) {
            emitAddToCartEvent(addToCartResult)
        }
    } catch {
        // no-op: add-to-cart should continue even if analytics fetch fails
    }

    emit('addToCart')
}

async function handleCompleteDocumentOrCheckout({
    submitter,
    documentInput
}: {
    submitter: HTMLElement | null
    documentInput: DocumentSectionFormInput
}) {
    const resolvedDocumentInput = makeDocumentInput(documentInput)

    if ((submitter as HTMLInputElement)?.value === 'payNow') {
        emit('inlineCheckout', resolvedDocumentInput)
    } else if ((submitter as HTMLInputElement)?.value === 'addToCart') {
        signDocument(
            {
                input: {
                    id: contractData.value.id,
                    updatedAtUtc: contractData.value.updatedAtUtc,
                    documentInput: resolvedDocumentInput
                },
                facilityId: activeFacility.value.id
            },
            {
                onSuccess: data => {
                    if (data.__typename === 'ValidationError') {
                        handleValidationErrors(
                            data.fieldErrors,
                            toRef(props, 'invalidInitials'),
                            toRef(props, 'invalidSignatures')
                        )
                        return
                    }

                    if (data.__typename === 'SignDocumentResult') {
                        // Update the cart data
                        if (data.query.viewer !== null) {
                            setCartData(data.query.viewer.cart)
                        }

                        if (data.record.__typename === 'PlanContract') {
                            emitAddToCartEvent(data.record)
                        }

                        emit('addToCart')
                        return
                    }

                    throw new Error()
                }
            }
        )
    } else if ((submitter as HTMLInputElement)?.value === 'checkout') {
        signDocumentCheckoutNow(
            {
                input: {
                    id: contractData.value.id,
                    updatedAtUtc: contractData.value.updatedAtUtc,
                    documentInput: resolvedDocumentInput
                },
                facilityId: activeFacility.value.id
            },
            {
                onSuccess: data => {
                    if (data.signDocument.__typename === 'ValidationError') {
                        handleValidationErrors(
                            data.signDocument.fieldErrors,
                            toRef(props, 'invalidInitials'),
                            toRef(props, 'invalidSignatures')
                        )
                        return
                    }

                    if (
                        data.createCheckout.__typename ===
                        'CreateCheckoutResult'
                    ) {
                        router.push(
                            facilityCheckoutLink(
                                activeFacility.value.slug,
                                data.createCheckout.recordId
                            )
                        )
                        if (cartVisible.value) {
                            setCartVisible(false)
                        }
                        return
                    } else if (
                        data.createCheckout.__typename ===
                        'CreateCheckoutIncomplete'
                    ) {
                        showAlert(
                            t('headsUp'),
                            data.createCheckout.results.includes(
                                ContractReadyForPurchase.COURSE_CANCELLED
                            )
                                ? t('cartSidebar.cancelledCourseCheckout')
                                : t(
                                      'purchaseContract.contractsNotReadyForPurchaseWarning',
                                      { count: data.createCheckout.count }
                                  )
                        )
                        invalidateCartQuery()
                        setCartVisible(true)
                        return
                    }

                    throw new Error()
                }
            }
        )
    }
}

function handleSubmit() {
    if (savedDocumentInput.value === undefined) {
        throw new Error()
    }

    activateContract(savedDocumentInput.value)
}

function handleSubmitNoPricingContract({
    documentInput
}: {
    documentInput: DocumentSectionFormInput
}) {
    const resolvedDocumentInput = makeDocumentInput(documentInput)

    if (isKiosk.value && !isAgreement.value) {
        emit('inlineCheckout', resolvedDocumentInput)
    } else {
        activateContract(resolvedDocumentInput)
    }
}

const toast = useToast()
const router = useRouter()
const { mutate: activateContractMutate, isPending: activatePending } =
    useActivateContractMutation()

const kioskSuccessFunction = useKioskSuccess()

const storefrontPrefix = useStorefrontPrefix()
const { facilityCheckoutLink, storefrontHomeLink } = useRouteLink()
function activateContract(documentInput: DocumentInput) {
    activateContractMutate(
        {
            id: contractData.value.id,
            updatedAtUtc: contractData.value.updatedAtUtc,
            documentInput
        },
        {
            onSuccess: data => {
                if (data.__typename === 'ValidationError') {
                    handleValidationErrors(
                        data.fieldErrors,
                        toRef(props, 'invalidInitials'),
                        toRef(props, 'invalidSignatures')
                    )
                    if (displayMode !== 'INLINE_MODE') {
                        display.value = true
                    }
                    return
                }

                if (isKiosk.value) {
                    if (
                        data.record.__typename === 'PlanContract' &&
                        data.record.startupInvoice !== undefined
                    ) {
                        kioskSuccessFunction({
                            contracts: [data.record],
                            invoice: data.record.startupInvoice
                        })
                    }
                    return
                } else if (isGroup.value) {
                    toast.add({
                        severity: 'success',
                        summary: 'Thank you for your submission!',
                        detail: contractName.value,
                        life: 10000,
                        closable: false
                    })
                    return router.push(
                        preserveModeLink(
                            '/' +
                                activeFacility.value.slug +
                                '?completedContract=' +
                                contractName.value
                        )
                    )
                } else {
                    toast.add({
                        severity: 'success',
                        summary: t('thankYouForSubmission'),
                        detail: contractName.value,
                        life: 10000,
                        closable: false
                    })
                    return router.push(
                        storefrontHomeLink(storefrontPrefix.value)
                    )
                }
            }
        }
    )
}

const invalidateCartQuery = useInvalidateCartQuery()
const showAlert = useShowAlert()
const { mutate: createCheckout, isPending: isCreatingCheckout } =
    useCreateCheckout()
async function handleCheckout() {
    createCheckout(activeFacility.value.id, {
        onSuccess: data => {
            if (data.createCheckout.__typename === 'CreateCheckoutResult') {
                router
                    .push(
                        facilityCheckoutLink(
                            activeFacility.value.slug,
                            data.createCheckout.recordId
                        )
                    )
                    .then(() => {
                        if (cartVisible.value) {
                            setCartVisible(false)
                        }
                    })
                return
            } else if (
                data.createCheckout.__typename === 'CreateCheckoutIncomplete'
            ) {
                showAlert(
                    t('headsUp'),
                    data.createCheckout.results.includes(
                        ContractReadyForPurchase.COURSE_CANCELLED
                    )
                        ? t('cartSidebar.cancelledCourseCheckout')
                        : t(
                              'purchaseContract.contractsNotReadyForPurchaseWarning',
                              {
                                  count: data.createCheckout.count
                              }
                          )
                )
                invalidateCartQuery()
                setCartVisible(true)
                return
            }

            throw new Error()
        }
    })
}

watch(
    () => props.isSigningDocument,
    (newValue, oldValue) => {
        if (!newValue && oldValue) {
            if (
                props.invalidInitials.size === 0 &&
                props.invalidSignatures.size === 0
            ) {
                display.value = false
            }

            if (
                isKiosk.value &&
                displayMode !== 'INLINE_MODE' &&
                (props.invalidInitials.size > 0 ||
                    props.invalidSignatures.size > 0)
            ) {
                display.value = true
            }
        }
    }
)

const contractHasPricing = computed(() => {
    return (
        contractData.value.terms.billingType === 'RECURRING' ||
        new Decimal(contractData.value.previewPricing.total).isNotZero()
    )
})

const contractHasFee = computed(() => {
    return new Decimal(contractData.value.previewPricing.total).isNotZero()
})

const inlineCheckoutLanguage = computed(() => {
    if (props.balanceDueMode) {
        return isAgreement.value
            ? t('purchaseContract.documentSection.submitAgreement')
            : t('purchaseContract.completeBooking')
    }

    return contractHasFee.value
        ? t('purchaseContract.documentSection.payNow')
        : t('purchaseContract.completeBooking')
})

const contractName = computed(() => {
    if (
        contractData.value.planPresentation === null ||
        contractData.value.planPresentation.title === ''
    ) {
        return contractData.value.plan.name
    }

    return contractData.value.planPresentation.title
})
</script>
